import { useState } from 'react';
import { Auth } from '../services/amplifyCompat';

const isAlreadyConfirmedError = (error) => {
  const code = error?.code || error?.name;
  return (
    code === 'NotAuthorizedException' &&
    error?.message?.includes('Current status is CONFIRMED')
  );
};

const getUserFriendlyMessage = (error) => {
  const code = error?.code || error?.name;
  switch (code) {
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'UserNotFoundException':
      return 'We couldn\u2019t find an account with that email. Please check your email address and try again.';
    case 'ExpiredCodeException':
      return 'Your confirmation code has expired. Please request a new one.';
    case 'CodeMismatchException':
      return 'Invalid confirmation code. Please check and try again.';
    case 'NotAuthorizedException':
      return 'Incorrect email or password. Please try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

export const useVerifyUser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const verifyUser = async ({ email, password, confirmation_code }) => {
    setError(null);
    setIsLoading(true);
    try {
      const normalizedEmail = email?.trim();
      const normalizedConfirmationCode = confirmation_code?.trim();
      let autoSignedIn = false;
      try {
        const confirmResult = await Auth.confirmSignUp(
          normalizedEmail,
          normalizedConfirmationCode
        );
        // If confirmSignUp triggered auto sign-in (COMPLETE_AUTO_SIGN_IN step),
        // amplifyCompat already called autoSignIn() so we don't need to sign in manually.
        if (confirmResult?.nextStep?.signUpStep === 'COMPLETE_AUTO_SIGN_IN' || confirmResult?.isSignedIn === true) {
          autoSignedIn = true;
        }
      } catch (confirmError) {
        if (!isAlreadyConfirmedError(confirmError)) {
          throw confirmError;
        }
      }
      if (!autoSignedIn) {
        await Auth.signIn(normalizedEmail, password);
      }

      setSuccess(true);
    } catch (error) {
      console.error('Error verifying user:', error);
      setError(getUserFriendlyMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return { verifyUser, isLoading, error, success };
};
